import * as React from 'react';

import { SlCheckbox } from '@shoelace-style/shoelace/dist/react';
import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { useAtom } from 'jotai';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { translate } from '~l10n/client';
import { Badge } from '~shared/components/Badge';
import { SmallButton } from '~shared/components/buttons';
import { useAutoFocus } from '~shared/components/helpers';
import { Link } from '~shared/components/Link';
import { SelectNumberRadioGroup } from '~shared/components/SelectNumberRadio/SelectNumberRadio';
import { toast } from '~shared/components/Toast';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { InformationIcon } from '~teacher/components/QuizEditor/GenerateQuestionDialog/InformationIcon';
import { checkIsPromptEmpty } from '~teacher/components/QuizEditor/GenerateQuestionDialog/utils';

import {
  checkIsQuestionTypeEmpty,
  generateQuestionAtom,
  promptAtom,
  questionCountOptions,
  useScopedStepAtom,
} from './atom';
import { GenerateQuestionPrompt } from './GenerateQuestionPrompt';
import { LearnMoreIcon } from './LearnMoreIcon';
import { useHandleGenerateQuestions } from './useHandleGenerateQuestion';

function useGenerateQuestion() {
  const [, changeStep] = useScopedStepAtom();
  const handleGenerateQuestions = useHandleGenerateQuestions();

  const [
    {
      [QuestionTypes.MultipleChoice]: multipleChoice,
      [QuestionTypes.TrueFalse]: trueFalse,
      [QuestionTypes.FreeResponse]: shortAnswers,
    },
  ] = useAtom(generateQuestionAtom);

  const [prompt] = useAtom(promptAtom);

  return () => {
    if (
      checkIsQuestionTypeEmpty(multipleChoice, trueFalse, shortAnswers) ||
      checkIsPromptEmpty(prompt)
    ) {
      toast.critical(translate('Required field(s) missing'));
      return;
    }

    handleGenerateQuestions();
    changeStep({ type: 'NextStep' });
  };
}

export function GenerateQuestionStep1() {
  const autoFocus = useAutoFocus();

  const [
    {
      [QuestionTypes.MultipleChoice]: multipleChoice,
      [QuestionTypes.TrueFalse]: trueFalse,
      [QuestionTypes.FreeResponse]: shortAnswers,
      questionCount,
      includeExplanation,
    },
    dispatch,
  ] = useAtom(generateQuestionAtom);

  const generateQuestion = useGenerateQuestion();

  return (
    <>
      <div className="pb-4">
        <div className="flex">
          <div className="flex flex-shrink flex-col">
            <div className="flex">
              <Link
                indicatorProps={{ isInset: false, notOffset: true }}
                href={
                  'https://help.socrative.com/en/articles/8732134-socrative-ai-question-generation'
                }
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline"
                onClick={() => {
                  new FeatureUseEventAdapter({
                    feature: 'ai_assisted_link',
                    source: 'generate_questions_step_1',
                    action: 'click',
                  }).sendAnalytics();
                }}
              >
                <Badge textTransform="capitalize" tone="ai">
                  {translate('AI Assisted')}
                  <LearnMoreIcon />
                </Badge>
              </Link>
            </div>
            <div className="font-display text-md leading-normal text-blue-900 font-700">
              {translate('Generate Questions')}
            </div>
            <div className="text-sans text-base leading-normal text-blue-600">
              {translate(
                'Write a detailed prompt describing the questions you wish to generate. You can include any excerpt, subject, standard, grade level, or area of study.'
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <div
          className="flex justify-between flex-col pt-4 gap-2"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <GenerateQuestionPrompt ref={autoFocus} />
        </div>
        <div
          className="flex flex-col gap-6 pt-5"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <div className="font-sans text-base text-blue-900">
            {translate('Question Type')}
          </div>
          <div className="flex flex-col gap-4 font-sans text-base">
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.MultipleChoice,
                    value: event.target.checked,
                  },
                });
              }}
              checked={multipleChoice}
              name={QuestionTypes.MultipleChoice}
            >
              <span>{translate('Multiple Choice')}</span>
            </SlCheckbox>
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.TrueFalse,
                    value: event.target.checked,
                  },
                });
              }}
              checked={trueFalse}
              name={QuestionTypes.TrueFalse}
            >
              <span>{translate('True / False')}</span>
            </SlCheckbox>
            <SlCheckbox
              size="medium"
              onSlChange={(event: any) => {
                dispatch({
                  type: 'QuestionTypeChange',
                  payload: {
                    questionType: QuestionTypes.FreeResponse,
                    value: event.target.checked,
                  },
                });
              }}
              checked={shortAnswers}
              name={QuestionTypes.FreeResponse}
            >
              <span>{translate('Short Answer')}</span>
            </SlCheckbox>
          </div>
        </div>
        <div
          className="pt-5"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <SlCheckbox
            checked={includeExplanation}
            onSlChange={(event: any) => {
              dispatch({
                type: 'IncludeExplanationChange',
                payload: { includeExplanations: event.target.checked },
              });
            }}
          >
            <span className="font-sans text-base">
              {translate('Generate Explanations')}
            </span>{' '}
            <TooltipV2
              label={translate(
                'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
              )}
            >
              <InformationIcon />
            </TooltipV2>
          </SlCheckbox>
        </div>
        <div
          className="pt-5"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <SelectNumberRadioGroup
            label={
              <span slot="label">
                {translate('Number of Questions')}{' '}
                <TooltipV2
                  label={translate(
                    'Only for multiple-choice and true / false. Explains the correct answer to students after they submit a response. You can choose to show explanations from your Launch settings.'
                  )}
                >
                  <InformationIcon />
                </TooltipV2>
              </span>
            }
            name="numberOfQuestions"
            selectedNumber={questionCount}
            numberOptions={questionCountOptions}
            onChange={(number) => {
              dispatch({
                type: 'NumberQuestionChange',
                payload: { value: number },
              });
            }}
          />
        </div>
        <div
          className="flex justify-end pt-8"
          style={{ boxShadow: '0px 1px 0px 0px #E7EDF0 inset' }}
        >
          <SmallButton appearance="primary" onClick={generateQuestion}>
            {translate('Generate')}
          </SmallButton>
        </div>
      </div>
    </>
  );
}
